import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ArrowLeft, ShoppingCart, Check, Info, Loader2, AlertCircle, 
  FlaskConical, Bug, Leaf, Microscope, Tag, ExternalLink 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons and colors for classes
const CLASS_CONFIG = {
  'Virus': { icon: FlaskConical, color: 'bg-red-100 text-red-700 border-red-200' },
  'Bacterium': { icon: Bug, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  'Fungus': { icon: Leaf, color: 'bg-green-100 text-green-700 border-green-200' },
  'Parasite': { icon: Microscope, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  'Viroid': { icon: FlaskConical, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  'Others': { icon: FlaskConical, color: 'bg-gray-100 text-gray-700 border-gray-200' },
  'Species Detection Assays': { icon: Tag, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

const TAG_COLORS = {
  'Human': 'bg-blue-50 text-blue-600 hover:bg-blue-100',
  'Veterinary': 'bg-green-50 text-green-600 hover:bg-green-100',
  'Plant': 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100',
  'Food': 'bg-orange-50 text-orange-600 hover:bg-orange-100',
  'Water': 'bg-cyan-50 text-cyan-600 hover:bg-cyan-100',
  'Environmental': 'bg-amber-50 text-amber-600 hover:bg-amber-100',
  'Industrial': 'bg-slate-50 text-slate-600 hover:bg-slate-100',
  'Laboratory control': 'bg-pink-50 text-pink-600 hover:bg-pink-100',
};

const PathogenDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();

  const [pathogen, setPathogen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  // Fetch pathogen details
  useEffect(() => {
    const fetchPathogen = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_URL}/api/pathogens/${id}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Pathogen not found');
          }
          throw new Error('Failed to fetch pathogen details');
        }
        
        const data = await response.json();
        setPathogen(data);
        
        // Set default selection to first option
        if (data.pricing_options && data.pricing_options.length > 0) {
          setSelectedOption(data.pricing_options[0].option_id);
        }
      } catch (err) {
        console.error('Error fetching pathogen:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPathogen();
    }
  }, [id]);

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (!selectedOption) {
      toast.error('Please select a product option');
      return;
    }

    const option = pathogen.pricing_options.find(o => o.option_id === selectedOption);
    if (!option) {
      toast.error('Invalid option selected');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart({
        technology: 'pathogen_detection',
        application: 'pathogen_kit',
        configuration: {
          pathogen_id: pathogen.id,
          pathogen_name: pathogen.species,
          pathogen_class: pathogen.pathogen_class,
          option_id: option.option_id,
          option_name: option.name,
          option_description: option.description
        },
        price: option.price,
        quantity: 1
      });
      toast.success(`${pathogen.species} - ${option.name} added to cart!`);
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  // Handle order now
  const handleOrderNow = async () => {
    await handleAddToCart();
    if (user) {
      navigate('/cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading pathogen details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/pathogens')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pathogen List
          </Button>
        </div>
      </div>
    );
  }

  if (!pathogen) {
    return null;
  }

  const classConfig = CLASS_CONFIG[pathogen.pathogen_class] || CLASS_CONFIG['Others'];
  const IconComponent = classConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="text-gray-300">/</span>
            <Link to="/pathogens" className="text-gray-500 hover:text-gray-700">Pathogens</Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium">{pathogen.species}</span>
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <CardContent className="pt-6">
                {/* Class Badge */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${classConfig.color} mb-4`}>
                  <IconComponent className="w-4 h-4" />
                  <span className="text-sm font-medium">{pathogen.pathogen_class}</span>
                </div>

                {/* Title */}
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{pathogen.species}</h1>

                {/* Related Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {pathogen.related_tags.map(tag => (
                    <Link 
                      key={tag} 
                      to={`/pathogens?tag=${encodeURIComponent(tag)}`}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors ${TAG_COLORS[tag] || 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Link>
                  ))}
                </div>

                {/* Description */}
                <div className="prose prose-gray max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">About This Pathogen</h3>
                  <p className="text-gray-600 leading-relaxed">{pathogen.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Product Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Product Information</CardTitle>
                <CardDescription>What's included with your detection kit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Primer & Probe Mix</h4>
                      <p className="text-sm text-gray-600">Specifically designed and validated for accurate detection</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Positive Control</h4>
                      <p className="text-sm text-gray-600">Included for result validation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Master Mix <span className="text-gray-400 font-normal">(optional)</span></h4>
                      <p className="text-sm text-gray-600">Ready-to-use formulation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Protocol</h4>
                      <p className="text-sm text-gray-600">Detailed instructions included</p>
                    </div>
                  </div>
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-sm text-gray-700 ml-2">
                    These products are for <strong>Research Use Only</strong>. They were not developed for in vitro diagnostics.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Pricing & Order */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Select Product Option</CardTitle>
                <CardDescription>Choose the kit size that fits your needs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={selectedOption} onValueChange={setSelectedOption}>
                  {pathogen.pricing_options.map((option) => (
                    <div 
                      key={option.option_id}
                      className={`relative flex items-start p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedOption === option.option_id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedOption(option.option_id)}
                    >
                      <RadioGroupItem 
                        value={option.option_id} 
                        id={option.option_id}
                        className="mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <Label htmlFor={option.option_id} className="font-medium text-gray-900 cursor-pointer">
                          {option.name}
                        </Label>
                        <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                        <p className="text-xl font-bold text-blue-600 mt-2">€{option.price}</p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>

                <div className="pt-4 space-y-3">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    size="lg"
                    onClick={handleOrderNow}
                    disabled={addingToCart || !selectedOption}
                    data-testid="order-now-btn"
                  >
                    {addingToCart ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 mr-2" />
                    )}
                    Order Now
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="lg"
                    onClick={handleAddToCart}
                    disabled={addingToCart || !selectedOption}
                    data-testid="add-to-cart-btn"
                  >
                    Add to Cart
                  </Button>
                </div>

                {!user && (
                  <p className="text-sm text-gray-500 text-center">
                    <Link to="/login" className="text-blue-600 hover:underline">Login</Link> or{' '}
                    <Link to="/signup" className="text-blue-600 hover:underline">Sign up</Link> to order
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Button variant="outline" onClick={() => navigate('/pathogens')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pathogen List
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PathogenDetail;
